
import { INavCustomization } from '../services/settingsservice';
import { fn } from '../utils';
import { IPageMetaData, IViewConfigurations, INavCustomizationInfo } from '../nav';
import { CompatibilityService } from '../services/compatibilityservice';

/**
 * Administrative Functionality, see NavPageAdminController.cs
 */
export class NavPageAdminService {
    static $inject = ["CompatibilityService", "NavCustomization"];
    constructor($c: CompatibilityService, navAccessConfig: INavCustomization | PromiseLike<INavCustomization>) {
        const prefix = "/infoma.web.admin/page/";

        function getConfig() {
            return $c.when(navAccessConfig).then((c) => {
                if (c &&  (c.ConfigName || c.NavUrl)) {
                    return {
                        navUrl: c.NavUrl,
                        configName: c.ConfigName
                    };
                } else {
                    return {
                        configName: "Default"
                    };
                }
            });
        }

        function buildUrl(name: string, parameters: any) {
            return prefix + name + "?" + fn.EncodeQueryData(parameters);
        }

        this.GetAvailablePageInfos = (params) => getConfig().then((config) =>
            $c.get<INavPageInfo[]>(buildUrl("GetAvailablePageInfos", $.extend({}, { navUrl: config.navUrl }, params)))).then(d => d.data);

        this.CleanupCustomization = (params) => getConfig().then((config) =>
            $c.post<{ Changed: boolean, Empty: boolean }>(buildUrl("CleanupCustomization", $.extend({}, { configName: config.configName }, params)), {})).then(d => d.data);

        this.SavePageCustomization = (p: IPageMetaData) => getConfig().then(config =>
            $c.post(buildUrl("SavePageCustomization", { navUrl: config.navUrl }), p)).then(fn.nullOp);

        this.ForceReSave = (params: { pageName: string }) => getConfig().then(config =>
            $c.post<IPageMetaData>(buildUrl("ForceReSave", $.extend({}, params, { navUrl: config.navUrl })), {})).then(d => d.data);

        this.GetViewConfiguration = (params) => getConfig().then(config =>
            $c.get<IViewConfigurations>(buildUrl("GetViewConfiguration", $.extend({}, config, params)))).then(d => d.data);

        this.SaveViewConfiguration = (viewConfig) => getConfig().then(config => {
            $c.clearCache();
            return $c.post<IViewConfigurations>(buildUrl("SaveViewConfiguration", { configName: config.configName }), viewConfig);
        }).then(d => d.data);

        this.GetCustomizationInfos = () => getConfig().then(config =>
            $c.get<INavCustomizationInfo[]>(buildUrl("GetCustomizationInfos", {}))).then(d => d.data);

        this.GetCustomizationInfo = () => getConfig().then(config =>
            $c.get<INavCustomizationInfo>(buildUrl("GetCustomizationInfo", config))).then(d => d.data);

        this.GetAllUserCustomizationInfo = () => getConfig().then(config => {
            return $c.get<IUserCustomizationEntry[]>(buildUrl("GetAllUserSettingsInfo", { configName: config.configName }));
        }).then(d => d.data);

    }

    /** Get Available Pages for a NAV Instance Url */
    public GetAvailablePageInfos: (params?: { forceCreate: boolean, navUrl?: string }) => PromiseLike<INavPageInfo[]>;

    /** 'Reduce' Customizations to actual Changes (will delete Customizations wOut Changes */
    public CleanupCustomization: (params: { pageName: string }) => PromiseLike<{ Changed: boolean, Empty: boolean }>;

    /** Save Page Customization from given Manipulated MetaData */
    public SavePageCustomization: (p: IPageMetaData) => PromiseLike<void>;

    /**Load (sloopy) and save (strict) Static Metadata Definition by PageName */
    public ForceReSave: (params: { pageName: string }) => PromiseLike<IPageMetaData>;

    /** Get View Vonfiguration for Customization */
    public GetViewConfiguration: (params?: { useGlobal?: boolean }) => PromiseLike<IViewConfigurations>;

    /** Save ViewConfiguration (empty object deletes Configuration!) */
    public SaveViewConfiguration: (configuration: IViewConfigurations) => PromiseLike<IViewConfigurations>;

    /** Get all Customization Infos */
    public GetCustomizationInfos: () => PromiseLike<INavCustomizationInfo[]>;

    /** Get Customization Info for Current Context */
    public GetCustomizationInfo: () => PromiseLike<INavCustomizationInfo>;

    /** Get Info about stored User Personalizations */
    public GetAllUserCustomizationInfo: () => PromiseLike<IUserCustomizationEntry[]>;
}

/** Info about RWF User from Stored Customizations */
export interface IUserCustomizationEntry {
    ID: string;
    DisplayName: string;
    LoginName: string;
    Name: string;
    LastAccess?: Date;
    Error?: string;
}

/** Administrative Description of PageInfo */
export interface INavPageInfo {
    PageName: string;
    Property: string;
    Key: string;
    /** Statically Loaded Information */
    Static: {
        Data?: IPageMetaData
        Error?: string;
    };
    Custom: {
        CustomData?: IPageMetaData
        HasData: boolean,
        Error?: string;
    };
}
